import React, { useEffect, forwardRef, useImperativeHandle, useRef  } from 'react';
import { webPrintA5Receipt } from '../utlilities/webPrint';


const NormalBillPrint1 = forwardRef(({
  invoiceNumber,
  selectedPatient,
  selectedDoctor,
  date,
  products,
  balance_due,
  calculateTotal,
  calculateGrandTotal,
  calculateTotalDiscount,
  calculateTotalGST,
  grand_total,
  setTotalPages,
  currentPage, 
  generalSetting,
  username,
  payingAmount,
  sysInfo,
  ipNo,
  mode
}, ref) => {
  const productsPerPage = 10; // Reduced to fit more columns 
  const barcodeRef = useRef(null);

  const validProducts = products.filter(product => product.id && product.name);

  const totalPages = Math.ceil(validProducts.length / productsPerPage);

  const roundAmount = (total) => {
    if ((total - Math.floor(total)) < 0.5) {
      return Math.floor(total);
    } else {
      return Math.ceil(total);
    }
  };
  // Calculate totals internally from products if functions are not provided
  const calculateGrandTotalInternal = () => {
    if (calculateGrandTotal) return calculateGrandTotal();
    if (calculateTotal) return calculateTotal();
    const total = validProducts.reduce((sum, product) => sum + parseFloat(product.totalAmount || 0), 0);

    if (generalSetting?.phValueRoundOff) {
      return roundAmount(total).toFixed(2);
    }
    return total.toFixed(2);
  };

  const calculateTotalDiscountInternal = () => {
    if (calculateTotalDiscount) return calculateTotalDiscount();
    const total = validProducts.reduce((sum, product) => sum + parseFloat(product.discountAmount || 0), 0);
    return total.toFixed(2);
  };

  const calculateTotalGSTInternal = () => {
    if (calculateTotalGST) return calculateTotalGST();
    const total = validProducts.reduce((sum, product) => sum + parseFloat(product.gstAmount || 0), 0);
    return total.toFixed(2);
  };

  const calculateTotalAmountInternal = () => {
    // Total amount before discount (sum of all product.amount)
    const total = validProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0);
    return total.toFixed(2);
  };

  // Convert number to words (Indian format)
  const numberToWords = (num) => {
    if (num === 0) return "Zero Rupees Only";
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 
                  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 
                  'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertHundreds = (n) => {
      if (n === 0) return '';
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
        if (n > 0) result += 'and ';
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result.trim();
    };
    
    const numStr = parseFloat(num).toFixed(2);
    const parts = numStr.split('.');
    const rupees = parseInt(parts[0]);
    const paise = parseInt(parts[1] || 0);
    
    let words = '';
    
    if (rupees === 0) {
      words = 'Zero';
    } else if (rupees < 100) {
      // 1-99: Direct conversion
      words = convertHundreds(rupees);
    } else if (rupees < 1000) {
      // 100-999: Hundreds
      words = convertHundreds(rupees);
    } else if (rupees < 100000) {
      // 1000-99999: Thousands and below
      const thousands = Math.floor(rupees / 1000);
      const remainder = rupees % 1000;
      
      if (thousands > 0) {
        words = convertHundreds(thousands) + ' Thousand';
        if (remainder > 0) {
          words += ' ' + convertHundreds(remainder);
        }
      } else {
        words = convertHundreds(remainder);
      }
    } else if (rupees < 10000000) {
      // 100000-9999999: Lakhs, Thousands, and below
      const lakhs = Math.floor(rupees / 100000);
      const remainder = rupees % 100000;
      
      words = convertHundreds(lakhs) + ' Lakh';
      
      if (remainder > 0) {
        if (remainder >= 1000) {
          const thousands = Math.floor(remainder / 1000);
          const remainder2 = remainder % 1000;
          words += ' ' + convertHundreds(thousands) + ' Thousand';
          if (remainder2 > 0) {
            words += ' ' + convertHundreds(remainder2);
          }
        } else {
          words += ' ' + convertHundreds(remainder);
        }
      }
    } else {
      // 10000000+: Crores, Lakhs, Thousands, and below
      const crores = Math.floor(rupees / 10000000);
      const remainder = rupees % 10000000;
      
      words = convertHundreds(crores) + ' Crore';
      
      if (remainder > 0) {
        if (remainder >= 100000) {
          const lakhs = Math.floor(remainder / 100000);
          const remainder2 = remainder % 100000;
          words += ' ' + convertHundreds(lakhs) + ' Lakh';
          
          if (remainder2 > 0) {
            if (remainder2 >= 1000) {
              const thousands = Math.floor(remainder2 / 1000);
              const remainder3 = remainder2 % 1000;
              words += ' ' + convertHundreds(thousands) + ' Thousand';
              if (remainder3 > 0) {
                words += ' ' + convertHundreds(remainder3);
              }
            } else {
              words += ' ' + convertHundreds(remainder2);
            }
          }
        } else if (remainder >= 1000) {
          const thousands = Math.floor(remainder / 1000);
          const remainder2 = remainder % 1000;
          words += ' ' + convertHundreds(thousands) + ' Thousand';
          if (remainder2 > 0) {
            words += ' ' + convertHundreds(remainder2);
          }
        } else {
          words += ' ' + convertHundreds(remainder);
        }
      }
    }
    
    words = words.trim() + ' Rupees';
    
    if (paise > 0) {
      words += ' and ' + convertHundreds(paise).trim() + ' Paise';
    }
    
    return words + ' Only';
  };

  useEffect(() => {
    setTotalPages(totalPages);
  }, [products]);

  const paginatedProducts = products.slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage);
  
  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;

  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }


  useEffect(() => {
      if (barcodeRef.current && invoiceNumber) {
          // Load JsBarcode from CDN
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
          script.async = true;
          script.onload = () => {
              if (window.JsBarcode) {
                  window.JsBarcode(barcodeRef.current, invoiceNumber, {
                      format: 'CODE128',
                      width: 1.5,
                      height: 25,
                      displayValue: false,
                      margin: 0
                  });
              }
          };
          document.body.appendChild(script);

          return () => {
              if (document.body.contains(script)) {
                  document.body.removeChild(script);
              }
          };
      }
  }, [invoiceNumber]);



  const handleDotMatrixPrint = async () => {
      const data = {
        settings: {
          cpi: 12,         // Condensed printing
          width: generalSetting?.phPrinterWidth || 80,       // Full width in condensed mode
          indent: 0,
          printerType: generalSetting?.phPrinterType || "TVS",// "EPSON" or "TVS"

          feedInLines: generalSetting?.phFeedinLines || 0,
          feedInForward: generalSetting?.phFeedInForward || 0,
          feedOutLines: generalSetting?.phFeedOutLines || 0,
          feedOutForward: generalSetting?.phFeedOutForward || 0,
          returnValidityDays: generalSetting?.phReturnValidityDays || 0,
        },
      
        header: {
          firmName:
            generalSetting?.phShowFirmname
              ? `${generalSetting?.FirstName || ""} ${generalSetting?.LastName || ""}`.trim()
              : "",
      
          phones:
            generalSetting?.phShowFirmnumber
              ? [generalSetting?.MobileNo1, generalSetting?.MobileNo2].filter(Boolean)
              : [],

          username:
            generalSetting?.phShowUsername && username
              ? String(username)
              : "",
      
          address:
            generalSetting?.phShowAddress
              ? [
                  // generalSetting?.AddressLine1,
                  // generalSetting?.AddressLine2,
                  generalSetting?.Place
                ].filter(Boolean)
              : [],
      
          workingTime:
            generalSetting?.phShowWorkingHourse && generalSetting?.WorkingTime
              ? String(generalSetting.WorkingTime)
              : "",
      
          // FIXED — guaranteed string
          gst:
            generalSetting?.phShowGst && generalSetting?.GstNo
              ? String(generalSetting.GstNo)
              : "",
      
          // FIXED — MUST BE STRING
          invoiceNumber: String(invoiceNumber ?? ""),
      
          // Format date from "19-Nov-2025 5:29 PM" to "19/11/2025 5:29 PM"
          date: (() => {
            if (!date) return "";
            try {
              const dateStr = String(date);
              // Parse date string like "19-Nov-2025 5:29 PM"
              const dateObj = new Date(dateStr);
              
              // Check if date is valid
              if (isNaN(dateObj.getTime())) {
                return dateStr; // Return original if parsing fails
              }
              
              // Format as DD/MM/YYYY HH:MM AM/PM
              const day = String(dateObj.getDate()).padStart(2, '0');
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const year = dateObj.getFullYear();
              
              // Get time part (HH:MM AM/PM)
              let hours = dateObj.getHours();
              const minutes = String(dateObj.getMinutes()).padStart(2, '0');
              const ampm = hours >= 12 ? 'PM' : 'AM';
              hours = hours % 12;
              hours = hours ? hours : 12; // 0 should be 12
              const timeStr = `${hours}:${minutes} ${ampm}`;
              
              return `${day}/${month}/${year}`;
            } catch (error) {
              return String(date ?? "");
            }
          })(),
      
          patientName: selectedPatient?.PatientName || "",
      
          // FIXED — MUST BE STRING
          age: selectedPatient?.Age ? String(selectedPatient.Age) : "",
      
          gender: selectedPatient?.Gender || "",
          doctor: selectedDoctor?.label || "",
          dlnum1: generalSetting?.dlnum1 || "",
          dlnum2: generalSetting?.dlnum2 || "",
          place: generalSetting?.Place || "",
          ipNo: ipNo || "",
          mode: mode || "",
        },
      
        products: products.map((p, i) => {
          // Format expiry date to MM/YY format
          let expFormatted = "";
          if (p.batch?.expiryDate) {
            const expDate = String(p.batch.expiryDate);
            // If date is in format "YYYY-MM-DD", convert to "MM/YY"
            if (expDate.includes('-')) {
              const parts = expDate.split('-');
              if (parts.length >= 2) {
                // parts[0] = year, parts[1] = month
                const year = parts[0].slice(-2); // Get last 2 digits of year
                expFormatted = `${parts[1]}/${year}`;
              } else {
                expFormatted = expDate;
              }
            } else {
              expFormatted = expDate;
            }
          }
          
          return {
            sr: ((currentPage - 1) * 6) + (i + 1),
            name: String(p.name || ""),
            batch: p.batch?.batchNo ? String(p.batch.batchNo).slice(-6) : "-",
            qty: Number(p.qty ?? 0),
            rate: Number(p.rate ?? 0),
            mrp: Number(p.mrp ?? p.batch?.mrp ?? 0),
            gstRate: Number(p.gstRate ?? p.batch?.gstRate ?? 0),
            gstAmount: Number(p.gstAmount ?? 0),
            discount: Number(p.discountAmount ?? 0),
            total: Number(p.totalAmount ?? 0),
            grandTotal: Number(p.amount ?? 0),
            exp: expFormatted
          };
        }),
      
        totals: {
          totalAmount: Number(calculateTotalAmountInternal() ?? 0),
          grandTotal: Number(calculateGrandTotalInternal() ?? 0),
          discount: Number(calculateTotalDiscountInternal() ?? 0),
          gst: Number(calculateTotalGSTInternal() ?? 0),
          paid: Number(payingAmount ?? 0),
          balance: generalSetting?.phValueRoundOff
            ? Number(roundAmount(balance_due))
            : Number(balance_due),
          balanceInWords: numberToWords(generalSetting?.phValueRoundOff
            ? roundAmount(balance_due)
            : balance_due)
        }
      };

      const phBillType = sysInfo?.phbilltype || generalSetting?.phBillType || '';

      if (isElectron() && ipcRenderer) {
        if(phBillType === 'Normal'){
          await ipcRenderer.invoke("print-a5ipreceipt", {
            printerName: sysInfo.pharmacyBillPriinter,
            json: data
          });
        } else{
          console.log(data, sysInfo.pharmacyBillPriinter);
          await ipcRenderer.invoke("print-ipreceipt", {
            printerName: sysInfo.pharmacyBillPriinter,
            json: data
          });
        }
      } else {
        webPrintA5Receipt(data, { title: 'PHARMACY CASH BILL' });
      }

  };
      

  useImperativeHandle(ref, () => ({
    handleDotMatrixPrint
  }));

  return (
    <div className="flex h-full bg-background">
        <div className="p-8 bg-gray-500">
            {/* Print Buttons */}
            <div className="mb-4 flex gap-4">
              <button
                onClick={handleDotMatrixPrint}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-semibold transition-colors"
              >
                Print (Dot Matrix Optimized)
              </button>
            </div>

            <div
                id="billDiv"
                style={{ page: "A5" }}
                className="h-[559px] w-[793px] bg-white flex flex-col justify-between"
            >
                <div className="p-2 flex-grow">
                    <div className="flex flex-col justify-center items-center">
                      <p className="text-sm font-semibold text-black">{generalSetting?.FirstName} {generalSetting?.LastName}</p>
                      <p className="text-sm text-black">{generalSetting?.AddressLine1} {generalSetting?.AddressLine2}</p>
                      <p className="text-sm text-black">Ph: {generalSetting?.MobileNo1} , {generalSetting?.MobileNo2}</p>
                      <p className="text-sm text-black">DL No. : {generalSetting?.dlnum1} , {generalSetting?.dlnum2}</p>
                    </div>

                    <p className="text-center text-black text-sm font-normal">PHARMACY IP CASH BILL</p>

                    <div className="flex mt-1 bg-white">
                        <div className="w-full">

                          {/* HEADER SECTION */}
                          <div className="flex justify-between w-full mt-1">

                              {/* LEFT SIDE */}
                              <div className="flex flex-col text-sm leading-tight w-1/2">

                                  {/* PATIENT + DOCTOR */}
                                  <div className="mt-1">
                                      <p>Patient : {selectedPatient?.PatientName} / {selectedPatient?.Age} {selectedPatient?.Gender}</p>
                                      <p>Doctor : {selectedDoctor?.label}</p>
                                      <p>IP No : {ipNo}</p>
                                  </div>
                              </div>

                              {/* RIGHT SIDE */}
                              <div className="flex flex-col text-sm leading-tight w-1/2 items-end justify-start">
                                  <p>Bill No : {invoiceNumber}</p>
                                  <p>Date : {date}</p>
                                  <p>Mode : {mode}</p>
                              </div>

                          </div>


                          <div className="mt-2">
                              {/* Fixed Table Header */}
                              <div className="flex w-full mb-1 text-xs">
                                  <div className="w-[6%]">
                                      <p className="text-black font-semibold text-xs text-left">Sr.</p>
                                  </div>
                                  <div className="w-[30%]">
                                      <p className="text-black font-semibold text-xs text-left">Product Name</p>
                                  </div>
                                  <div className="w-[10%]">
                                      <p className="text-black font-semibold text-xs text-left">Batch</p>
                                  </div>
                                  <div className="w-[6%]">
                                      <p className="text-black font-semibold text-xs text-center">Qty</p>

                                  </div>
                                  <div className="w-[8%]">
                                      <p className="text-black font-semibold text-xs text-left ml-5">Expiry</p>
                                  </div>
                                  <div className="w-[9%]">
                                      <p className="text-black font-semibold text-xs text-left ml-5">Rate</p>
                                  </div>
                                  <div className="w-[6%]">
                                      <p className="text-black font-semibold text-xs text-left ml-5">Disc</p>
                                  </div>
                                  <div className="w-[6%]">
                                      <p className="text-black font-semibold text-xs text-left ml-5">GST%</p>
                                  </div>
                                  <div className="w-[8%]">
                                      <p className="text-black font-semibold text-xs text-left ml-5">GST</p>
                                  </div>
                                  <div className="w-[9%]">
                                      <p className="text-black font-semibold text-xs text-left ml-5">Total</p>
                                  </div>
                              </div>
                              
                              <div className="w-full h-0.5 bg-black"></div>

                              {/* Fixed Product List */}
                              <div className="flex flex-col gap-1 mt-1 flex-grow w-full">
                                  {paginatedProducts.map((product, index) => {
                                      if (!product?.name) return null;
                                      const serialNumber =
                                          (currentPage - 1) * productsPerPage + (index + 1);

                                      // Format expiry date to MM/YYYY format
                                      let expFormatted = "";
                                      if (product.batch?.expiryDate) {
                                          const expDate = String(product.batch.expiryDate);
                                          // If date is in format "YYYY-MM-DD", convert to "MM/YYYY"
                                          if (expDate.includes('-')) {
                                              const parts = expDate.split('-');
                                              if (parts.length >= 2) {
                                                  // parts[0] = year, parts[1] = month
                                                  expFormatted = `${parts[1]}/${parts[0]}`;
                                              } else {
                                                  expFormatted = expDate;
                                              }
                                          } else {
                                              expFormatted = expDate;
                                          }
                                      }

                                      return (
                                          <div
                                              key={index}
                                              className="flex w-full text-xs"
                                          >
                                              <div className="w-[6%]">
                                                  <p className="text-black font-semibold text-xs text-left">
                                                      {serialNumber}
                                                  </p>
                                              </div>
                                              <div className="w-[30%]">
                                                  <p className="text-black font-semibold text-xs text-left">
                                                      {product?.name}
                                                  </p>
                                              </div>
                                              <div className="w-[10%]">
                                                  <p className="text-black font-semibold text-xs text-left">
                                                      {product?.batch?.batchNo ? String(product.batch.batchNo).slice(-6) : '-'}
                                                  </p>
                                              </div>
                                              <div className="w-[6%]">
                                                  <p className="text-black font-semibold text-xs text-center">
                                                      {product?.qty || '-'}
                                                  </p>
                                              </div>
                                              <div className="w-[8%]">
                                                  <p className="text-black font-semibold text-xs text-left ml-5">
                                                      {expFormatted || '-'}
                                                  </p>
                                              </div>
                                              <div className="w-[9%]">
                                                  <p className="text-black font-semibold text-xs text-left ml-5">
                                                      {product?.amount ? `₹${parseFloat(product.amount).toFixed(2)}` : '-'}
                                                  </p>
                                              </div>
                                              <div className="w-[6%]">
                                                  <p className="text-black font-semibold text-xs text-left ml-5">
                                                      {product?.discountAmount ? `₹${parseFloat(product.discountAmount).toFixed(2)}` : '-'}
                                                  </p>
                                              </div>
                                              <div className="w-[6%]">
                                                  <p className="text-black font-semibold text-xs text-left ml-5">
                                                      {product?.gstRate || product?.batch?.gstRate ? `${product.gstRate || product.batch.gstRate}%` : '-'}
                                                  </p>
                                              </div>
                                              <div className="w-[8%]">
                                                  <p className="text-black font-semibold text-xs text-left ml-5">
                                                      {product?.gstAmount ? `₹${parseFloat(product.gstAmount).toFixed(2)}` : '-'}
                                                  </p>
                                              </div>
                                              <div className="w-[9%]">
                                                  <p className="text-black font-semibold text-xs text-left ml-5">
                                                      {product?.totalAmount ? `₹${parseFloat(product.totalAmount).toFixed(2)}` : '-'}
                                                  </p>
                                              </div>
                                          </div>
                                      );
                                  })}
                              </div>
                          </div>
                        </div>
                    </div>
                </div>

                <div className="w-full h-0.5 bg-black"></div>

                {/* Footer Section with Line and Amounts */}
                <div className="w-full pb-5">
                    {/* Amounts Section */}
                    <div className="flex flex-row gap-10 p-2 justify-between items-start">
                        {/* Printed By Line */}
                        <div className="flex flex-col justify-between w-full">
                            <div className="flex flex-col">
                                <p className="text-sm font-bold text-black">
                                    You have saved : ₹{parseFloat(calculateTotalDiscountInternal()).toFixed(2)}
                                </p>
                                <p className="text-sm text-black italic">
                                   Amount to Pay : {numberToWords(parseFloat(calculateTotalAmountInternal()) - parseFloat(calculateTotalDiscountInternal()))}
                                </p>
                            </div>
                            {generalSetting?.phShowUsername && username && (
                                <p className="text-xs text-black">Printed by: {username}</p>
                            )}
                        </div>

                        <div className="flex flex-col gap-0.5 text-right w-full">
                            <p className="text-sm font-bold text-black">Grand Total</p>
                            <p className="text-sm font-bold text-black">Total Discount</p>
                            <p className="text-sm font-bold text-black">Total GST</p>
                            <p className="text-sm font-bold text-black">Net Total</p>
                        </div>
                        <div className="flex flex-col gap-0.5 text-right">
                            <p className="text-sm font-bold text-black">
                                ₹{parseFloat(calculateTotalAmountInternal()).toFixed(2)}
                            </p>
                            <p className="text-sm font-bold text-black">
                                ₹{parseFloat(calculateTotalDiscountInternal()).toFixed(2)}
                            </p>
                            <p className="text-sm font-bold text-black">
                                ₹{parseFloat(calculateTotalGSTInternal()).toFixed(2)}
                            </p>
                            <div className="flex flex-col">
                                <p className="text-sm font-bold text-black">
                                    ₹{(parseFloat(calculateTotalAmountInternal()) - parseFloat(calculateTotalDiscountInternal())).toFixed(2)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Line at the bottom */}
                    <div className="w-full h-0.5 bg-black"></div>
                </div>
            </div>
        </div>
    </div>
  );
});

export default NormalBillPrint1;